// Identity editor: app name (per language), short name, logo URLs, splash
// background colour, tagline. Uploads aren't wired yet — paste a URL for now;
// Phase 1 will add storage upload via IStorageService.

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Label } from '@erp/ui';
import type { Identity } from '@erp/app-builder';
import { LogoUploader } from '../LogoUploader';

interface IdentityTabProps {
  value: Identity;
  onChange: (next: Identity) => void;
}

export function IdentityTab({ value, onChange }: IdentityTabProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-6 max-w-2xl">
      <Section title={t('appBuilder.identity.namesTitle', { defaultValue: 'Nom de l’application' })}>
        <Field label="Français">
          <Input
            value={value.name.fr}
            onChange={(e) => onChange({ ...value, name: { ...value.name, fr: e.target.value } })}
            placeholder="Mon ERP"
          />
        </Field>
        <Field label="العربية">
          <Input
            value={value.name.ar ?? ''}
            onChange={(e) => onChange({ ...value, name: { ...value.name, ar: e.target.value } })}
            placeholder="إي آر بي"
            dir="rtl"
          />
        </Field>
        <Field label="English">
          <Input
            value={value.name.en ?? ''}
            onChange={(e) => onChange({ ...value, name: { ...value.name, en: e.target.value } })}
            placeholder="My ERP"
          />
        </Field>
        <Field
          label={t('appBuilder.identity.shortName', { defaultValue: 'Nom court' })}
          hint={t('appBuilder.identity.shortNameHint', { defaultValue: 'Affiché en petit (notifications, écran de chargement). Max 20 caractères.' })}
        >
          <Input
            value={value.shortName}
            onChange={(e) => onChange({ ...value, shortName: e.target.value.slice(0, 20) })}
            placeholder="Gesty"
            maxLength={20}
          />
        </Field>
      </Section>

      <Section title={t('appBuilder.identity.brandingTitle', { defaultValue: 'Logo & écran de chargement' })}>
        <LogoUploader
          variant="logo"
          value={value.logoUrl}
          onChange={(logoUrl) => onChange({ ...value, logoUrl })}
          label={t('appBuilder.identity.logoUrl', { defaultValue: 'Logo (clair)' })}
          hint={t('appBuilder.identity.logoUrlHint', { defaultValue: 'PNG/SVG carré 512×512 recommandé. Max 4 Mo.' })}
        />
        <LogoUploader
          variant="logo-dark"
          value={value.logoDarkUrl}
          onChange={(logoDarkUrl) => onChange({ ...value, logoDarkUrl })}
          label={t('appBuilder.identity.logoDarkUrl', { defaultValue: 'Logo (mode sombre)' })}
          hint={t('appBuilder.identity.logoDarkHint', { defaultValue: 'Optionnel — utilisé si défini, sinon le logo clair est réutilisé.' })}
        />
        <LogoUploader
          variant="splash"
          value={value.splashLogoUrl}
          onChange={(splashLogoUrl) => onChange({ ...value, splashLogoUrl })}
          label={t('appBuilder.identity.splashLogo', { defaultValue: 'Logo écran de chargement' })}
          hint={t('appBuilder.identity.splashHint', { defaultValue: 'Image affichée au démarrage. 1024×1024 recommandé.' })}
        />
        <Field
          label={t('appBuilder.identity.splashBg', { defaultValue: 'Couleur d’arrière-plan splash' })}
        >
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={value.splashBgColor}
              onChange={(e) => onChange({ ...value, splashBgColor: e.target.value })}
              className="h-10 w-16 rounded border border-input cursor-pointer"
            />
            <Input
              value={value.splashBgColor}
              onChange={(e) => onChange({ ...value, splashBgColor: e.target.value })}
              className="font-mono"
              placeholder="#1B2A4A"
            />
          </div>
        </Field>
      </Section>

      <Section title={t('appBuilder.identity.taglineTitle', { defaultValue: 'Slogan (optionnel)' })}>
        <Field label="Français">
          <Input
            value={value.tagline?.fr ?? ''}
            onChange={(e) => onChange({
              ...value,
              tagline: { ...(value.tagline ?? { fr: '' }), fr: e.target.value },
            })}
            placeholder="Gestion commerciale tunisienne"
          />
        </Field>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      <div className="space-y-3 pl-1">{children}</div>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}
